import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import type { Role, TenantUser } from '@iot-dashboard/shared';
import { useInviteUser, useRemoveUser, useResendInvite, useResetUserMfa, useUpdateUserRole, useUsers } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { FormRow } from '../../components/Form';
import { ConfirmDialog } from '../../components/Modal';
import { Panel } from '../../components/Panel';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { TimeAgo } from '../../components/TimeAgo';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';

type TenantRole = Exclude<Role, 'msp-admin'>;
const ROLE_LABEL: Record<TenantRole, string> = { 'tenant-admin': 'Administrator', 'tenant-viewer': 'Read-only' };
const STATUS_LABEL: Record<string, string> = { CONFIRMED: 'Active', FORCE_CHANGE_PASSWORD: 'Invited – not yet signed in', RESET_REQUIRED: 'Password reset required', DELETED: 'Deleted', UNKNOWN: 'Unknown' };

export function AdministratorsPage() {
  const { tenantId, tenant, canConfigure, me, base } = useTenantContext();
  const users = useUsers(tenantId, canConfigure);
  const invite = useInviteUser(tenantId);
  const resend = useResendInvite(tenantId);
  const changeRole = useUpdateUserRole(tenantId);
  const remove = useRemoveUser(tenantId);
  const resetMfa = useResetUserMfa(tenantId);
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('tenant-viewer');
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<TenantUser | null>(null);
  const [resetting, setResetting] = useState<TenantUser | null>(null);

  if (!canConfigure) return <Navigate to={`${base}/overview`} replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const u = await invite.mutateAsync({ email: email.trim(), role });
      toast.push(`Invitation sent to ${u.email}`, 'success');
      setEmail('');
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const columns: Column<TenantUser>[] = [
    { key: 'email', header: 'Email', primary: true, sortValue: (u) => u.email, render: (u) => <span className="cell-primary">{u.email}{u.email === me.email ? <span className="muted"> (you)</span> : null}</span> },
    { key: 'role', header: 'Role', sortValue: (u) => u.role, render: (u) => (
      <select className="select" value={u.role} disabled={changeRole.isPending || u.email === me.email} aria-label={`Role for ${u.email}`} onChange={(e) => changeRole.mutateAsync({ username: u.username, role: e.target.value as TenantRole }).then(() => toast.push('Role updated', 'success')).catch((err) => toast.push(errorMessage(err), 'error'))}>
        <option value="tenant-admin">{ROLE_LABEL['tenant-admin']}</option>
        <option value="tenant-viewer">{ROLE_LABEL['tenant-viewer']}</option>
      </select>
    ) },
    { key: 'status', header: 'Status', sortValue: (u) => u.userStatus, render: (u) => STATUS_LABEL[u.userStatus] ?? u.userStatus },
    { key: 'mfa', header: 'MFA', sortValue: (u) => (u.mfaEnabled ? 1 : 0), render: (u) => (u.mfaEnabled ? <span className="badge badge--info">On</span> : <span className="muted">Off</span>) },
    { key: 'invited', header: 'Invited', sortValue: (u) => u.invitedAt, render: (u) => <div><TimeAgo iso={u.invitedAt} /><div className="cell-sub">by {u.invitedBy}</div></div> },
    { key: 'actions', header: '', render: (u) => (
      <span className="row" style={{ justifyContent: 'flex-end' }}>
        {u.userStatus === 'FORCE_CHANGE_PASSWORD' ? <button type="button" className="btn btn--sm" disabled={resend.isPending} onClick={() => resend.mutateAsync(u.username).then(() => toast.push('Invitation re-sent', 'success')).catch((err) => toast.push(errorMessage(err), 'error'))}>Resend invite</button> : null}
        {u.mfaEnabled ? <button type="button" className="btn btn--sm" onClick={() => setResetting(u)}>Reset MFA</button> : null}
        {u.email !== me.email ? <button type="button" className="btn btn--sm btn--danger" onClick={() => setRemoving(u)}>Remove</button> : null}
      </span>
    ) },
  ];

  return (
    <>
      <PageHeader title="Administrators" subtitle={`People who can sign in to the ${tenant.name} dashboard. Invitations are emailed with a temporary password.`} />
      <Panel title="Invite a user">
        <form className="form" onSubmit={submit}>
          {error ? <div className="form-error">{error}</div> : null}
          <FormRow label="Email" required htmlFor="inv-email"><input id="inv-email" className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@client.example" /></FormRow>
          <FormRow label="Role" htmlFor="inv-role" hint="Administrators manage users, alert profiles and the integration. Read-only users can view everything but change nothing.">
            <select id="inv-role" className="select" value={role} onChange={(e) => setRole(e.target.value as TenantRole)}>
              <option value="tenant-viewer">{ROLE_LABEL['tenant-viewer']}</option>
              <option value="tenant-admin">{ROLE_LABEL['tenant-admin']}</option>
            </select>
          </FormRow>
          <div className="form-actions"><button type="submit" className="btn btn--primary" disabled={invite.isPending}>{invite.isPending ? 'Sending…' : 'Send invitation'}</button></div>
        </form>
      </Panel>
      <Panel title="Users" flush>
        {users.isPending ? <Loading /> : users.isError ? <ErrorState error={users.error} retry={() => users.refetch()} /> : (
          <DataTable columns={columns} rows={users.data} rowKey={(u) => u.username} defaultSort={{ key: 'email', dir: 'asc' }} empty={<div className="empty"><h3>No users yet</h3><p>Invite the client's first administrator above.</p></div>} />
        )}
      </Panel>
      {resetting ? (
        <ConfirmDialog title="Reset multi-factor authentication" message={<>Remove the authenticator app registered by <strong>{resetting.email}</strong>? Use this when they have lost their phone. They can enrol a new app from Account &amp; security after signing in{'.'}</>} confirmLabel="Reset MFA" danger busy={resetMfa.isPending} onConfirm={() => resetMfa.mutateAsync(resetting.username).then(() => { toast.push('Authenticator removed', 'success'); setResetting(null); }).catch((err) => toast.push(errorMessage(err), 'error'))} onClose={() => setResetting(null)} />
      ) : null}
      {removing ? (
        <ConfirmDialog title="Remove user" message={<>Remove <strong>{removing.email}</strong>? They will no longer be able to sign in.</>} confirmLabel="Remove" danger busy={remove.isPending} onConfirm={() => remove.mutateAsync(removing.username).then(() => { toast.push('User removed', 'success'); setRemoving(null); }).catch((err) => toast.push(errorMessage(err), 'error'))} onClose={() => setRemoving(null)} />
      ) : null}
    </>
  );
}
